/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";

import * as Styled from "./styles";

import {
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { ReturnButton } from "components/ReturnButton";
import { useAuth } from "shared/providers/auth";
import { useParams } from "react-router-dom";
import { getClinicalRecords } from "requests/ClinicalRecord";
import { getAnamnesePatient } from "requests/Anamnese";
import { ClinicalRecord } from "shared/types/ClinicalRecord";

import { IoIosArrowUp } from "react-icons/io";
import { FaFilter, FaNotesMedical } from "react-icons/fa";
import { GalleryDocuments } from "components/Gallery";
import { ModalSignature } from "components/ModalSignature";
import {
  getConsultationTypeColor,
  normalizeConsultationType,
} from "shared/constants/consultationTypes";

interface WireDescriptions {
  [key: number]: string;
}

const typeMap: Record<number, string> = {
  55: "Colagem Inferior",
  56: "Colagem Superior",
  2: "1ª Consulta",
  285: "Colagem de Tubo",
  30: "Banda Inferior",
  31: "Banda Superior",
  7: "Alta",
  201: "Remoção de Aparelho",
  136: "Manutenção",
  127: "Instalação Aparelho Móvel Inferior",
  128: "Instalação Aparelho Móvel Superior",
  492: "Instalação de Aparelho Móvel",
  144: "Moldagem Inferior",
  146: "Moldagem Superior",
  493: "Moldagem do Aparelho",
  491: "Manutenção",
  65: "Controle de Contenção",
  490: "Manutenção",
  134: "Manutenção",
  138: "Manutenção",
  440: "Manutenção",
  266: "Manutenção",
  244: "Separador",
  290: "Instalação de Contenção Superior e Inferior",
  294: "Instalação de Contenção Superior",
  295: "Instalação de Contenção Inferior",
  82: "Emergência",
  11: "Avaliação",
};

const anamneseLabels = [
  { label: "Faz uso de medicamento?", identify: "OCQ13" },
  { label: "Anomalia de nascença?", identify: "OCQ14" },
  { label: "Cirurgia anterior?", identify: "OCQ15" },
  { label: "A recuperação ocorreu como esperado?", identify: "OCQ25" },
  { label: "Teve Hemorragia?", identify: "OCQ26" },
  { label: "Já foi anestesiado?", identify: "OCQ16" },
  { label: "Distúrbios respiratórios?", identify: "OCQ17" },
  { label: "Doenças sanguíneas?", identify: "OCQ18" },
  { label: "Doença óssea?", identify: "OCQ19" },
  { label: "Problemas endócrinos?", identify: "OCQ20" },
  { label: "Doenças cardíacas?", identify: "OCQ21" },
  { label: "Possui alguma alergia?", identify: "OCQ22" },
  { label: "Traumatismo de face ou cabeça?", identify: "OCQ23" },
  { label: "Fraturas ósseas?", identify: "OCQ24" },
  { label: "A recuperação aconteceu como esperado?", identify: "OCQ27" },

  // Adicionando as opções
  { label: "Asma", identify: "OCQ1" },
  { label: "Anemia", identify: "OCQ2" },
  { label: "Febre reumática", identify: "OCQ3" },
  { label: "Epilepsia", identify: "OCQ4" },
  { label: "Distúrbios auditivos", identify: "OCQ5" },
  { label: "Herpes", identify: "OCQ6" },
  { label: "Hepatite", identify: "OCQ7" },
  { label: "Diabete", identify: "OCQ8" },
  { label: "HIV", identify: "OCQ9" },
  { label: "Gestante", identify: "OCQ10" },
  { label: "Fumante", identify: "OCQ11" },
  { label: "Hipertensão", identify: "OCQ12" },
];

const getLabelById = (id: string) => {
  const item = anamneseLabels.find((entry) => entry.identify === id);
  return item ? item.label : id; // Retorna o id se não encontrar o label
};

/**
 * Verifica se o registro possui anamnese preenchida
 */
const hasAnamnese = (record: ClinicalRecord): boolean =>
  Boolean(record?.doc?.anamnese && record.doc.anamnese.length > 0);

/**
 * Formata a data da anamnese vinda do banco (yyyy-mm-dd hh:mm:ss)
 */
const formatAnamneseDate = (date: string | null): string | undefined => {
  if (!date) {
    return undefined;
  }

  const parsed = new Date(date.replace(" ", "T"));

  if (isNaN(parsed.getTime())) {
    return undefined;
  }

  return parsed.toLocaleDateString("pt-BR");
};

/**
 * Lista de respostas da anamnese, reutilizada na ficha e no modal de anamnese
 */
const AnamneseItems: React.FC<{ items: any[] }> = ({ items }) => (
  <>
    {items.map((item: any, index: any) => (
      <React.Fragment key={index}>
        {/* Tratamento especial para condições neurológicas */}
        {item.id === "NEURO" || item.hasSpecialNeeds ? (
          <Box
            sx={{
              backgroundColor: "#fff3e0",
              border: "2px solid #ffb74d",
              borderRadius: "8px",
              padding: "12px",
              margin: "10px 0",
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                color: "#e65100",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              ⚠️ Condições Neurológicas e Transtornos
            </Typography>
            {item.description && (
              <Typography sx={{ marginTop: "5px" }}>
                {item.description}
              </Typography>
            )}
            {item.otherConditions && (
              <Typography sx={{ marginTop: "5px", fontStyle: "italic" }}>
                Outras: {item.otherConditions}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <Typography sx={{ fontWeight: "bold !important" }}>
              {getLabelById(item.id)}
            </Typography>

            <Typography>{item.option === 1 ? "Sim" : "Não"}</Typography>

            {item.observation && (
              <Typography>Observação: {item.observation}</Typography>
            )}

            {item.description && (
              <Typography>Observação: {item.description}</Typography>
            )}
          </>
        )}
      </React.Fragment>
    ))}
  </>
);

const ClinicalRecordUser: React.FC<{
  idPacientParam?: number;
  onClick?: () => void;
}> = ({ idPacientParam, onClick }) => {
  const { user } = useAuth();
  const { idPacient } = useParams();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [records, setRecords] = React.useState<ClinicalRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = React.useState<ClinicalRecord[]>([]);
  const [hasIncomplete, setHasIncomplete] = React.useState<boolean>(false);
  const [recordOpen, setRecordOpen] = React.useState<ClinicalRecord>(
    {} as ClinicalRecord
  );
  const [filterType, setFilterType] = React.useState<string>("Todos");
  const [availableTypes, setAvailableTypes] = React.useState<string[]>([]);
  const [openAnamnese, setOpenAnamnese] = React.useState<boolean>(false);
  const [patientAnamnese, setPatientAnamnese] = React.useState<{
    items: any[];
    date: string | null;
  } | null>(null);

  const pacId = Number(
    idPacientParam && idPacientParam > 0 ? idPacientParam : idPacient
  );

  /**
   * Determina o tipo de consulta baseado nos dados do registro
   */
  const determineConsultationType = (record: ClinicalRecord): string => {
    // PRIORIDADE 1: Usa o procedimento real da agenda (vindo do backend)
    if (record.procedureName) {
      return record.procedureName;
    }

    // PRIORIDADE 2: Usa o ID do procedimento se disponível
    if (record.procedureId && typeMap[record.procedureId]) {
      return typeMap[record.procedureId];
    }

    // PRIORIDADE 3: Verifica se tem diagnóstico e anamnese (indica 1ª consulta)
    if (
      (record.doc?.diagnosis && record.doc.diagnosis.length > 0) ||
      (record.doc?.anamnese && record.doc.anamnese.length > 0)
    ) {
      return "1ª Consulta";
    }

    // PRIORIDADE 4: Verifica nos procedimentos realizados
    if (record.doc?.procedures && record.doc.procedures.length > 0) {
      const firstProcedure = record.doc.procedures.find(
        (p: any) => p.question && p.type !== "NOT"
      );
      if (firstProcedure?.question) {
        // Tenta identificar o tipo baseado na descrição do procedimento
        const question = firstProcedure.question.toLowerCase();
        if (question.includes("contenção")) return "Controle de Contenção";
        if (question.includes("colagem")) return "Colagem";
        if (question.includes("fio")) return "Manutenção";
        if (question.includes("banda")) return "Banda";
        if (question.includes("moldagem")) return "Moldagem";
      }
    }

    // PRIORIDADE 5: Verifica no planning da próxima consulta
    if (record.doc?.planning && record.doc.planning.length > 0) {
      const planningId = record.doc.planning[0]?.id;
      if (planningId === "PPQ001") return "Colagem";
      if (planningId === "PPQ002" || planningId === "PPQ003") return "Manutenção";
      if (planningId === "PPQ004") return "Banda";
      if (planningId === "PPQ005") return "Colagem de Tubo";
      if (planningId === "PPQ020") return "Controle de Contenção";
    }

    // PRIORIDADE 6: Default para Manutenção se houver algum procedimento
    if (record.doc?.procedures && record.doc.procedures.length > 0) {
      return "Manutenção";
    }

    return "Outros";
  };

  const clinicalRecordsAll = async () => {
    setLoading(true);

    try {
      const { status, data } = await getClinicalRecords({
        userId: user.data.id,
        idPac: pacId,
      });

      if ([200, 201].includes(status)) {
        const findHasIncomplete = data.data.filter(
          (item) => item.hasIncompleteSignatures === true
        );

        if (findHasIncomplete.length > 0) {
          setHasIncomplete(true);
        }

        // Enriquece os registros com o tipo de consulta
        const enrichedRecords = data.data.map((record) => {
          const consultationType = determineConsultationType(record);
          return {
            ...record,
            consultationType,
            consultationTypeNormalized: normalizeConsultationType(consultationType),
          };
        });

        // Extrai os tipos únicos disponíveis para o filtro
        const uniqueTypes = Array.from(
          new Set(enrichedRecords.map((r) => r.consultationTypeNormalized))
        ).filter(Boolean) as string[];
        setAvailableTypes(uniqueTypes.sort());

        setRecords(enrichedRecords);
        setFilteredRecords(enrichedRecords);
      } else {
        throw new Error("Erro ao carregar as fotos.");
      }
    } catch (e: any) {
      console.error("Erro ao carregar fichas clínicas:", e);
    }
    setLoading(false);
  };

  // Filtrar registros quando o filtro mudar
  React.useEffect(() => {
    if (filterType === "Todos") {
      setFilteredRecords(records);
    } else {
      setFilteredRecords(
        records.filter((r) => r.consultationTypeNormalized === filterType)
      );
    }
  }, [filterType, records]);

  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    setFilterType(event.target.value);
  };

  React.useEffect(() => {
    clinicalRecordsAll();
  }, []);

  // Busca a anamnese do paciente (tabela anamnese), independente da ficha
  React.useEffect(() => {
    const loadAnamnese = async () => {
      if (!pacId || pacId <= 0) {
        return;
      }

      try {
        const { status, data } = await getAnamnesePatient({
          userId: user.data.id,
          idPac: pacId,
        });

        if ([200, 201].includes(status) && data.data && data.data.data?.length) {
          setPatientAnamnese({ items: data.data.data, date: data.data.date });
        } else {
          setPatientAnamnese(null);
        }
      } catch (e: any) {
        console.error("Erro ao carregar anamnese do paciente:", e);
      }
    };

    loadAnamnese();
  }, [pacId, user.data.id]);

  const getDescription = (code: string) => {
    if (code === "PPQ001") {
      return "Colagem da Aparatologia Ortodôntica";
    }
    if (code === "PPQ002") {
      return "Trocar Fio";
    }
    if (code === "PPQ003") {
      return "Manter Fio";
    }
    if (code === "PPQ004") {
      return "Cimentação de banda";
    }
    if (code === "PPQ005") {
      return "Colagem do tubo";
    }
    if (code === "PPQ007") {
      return "Aparelho Complementar";
    }
    if (code === "PPQ008") {
      return "Outros procedimentos";
    }
    if (code === "PPQ009") {
      return "Encaminhamentos";
    }
    if (code === "PPQ015") {
      return "Aparelho Móvel";
    }
    if (code === "PPQ016") {
      return "Realizou a moldagem";
    }
    return null;
  };

  const positionDescriptions = {
    0: "Inferior",
    1: "Superior",
    3: "Superior e Inferior",
  };

  const typeDescriptions: WireDescriptions = {
    10: "10 Redondo",
    1: "12 Redondo",
    2: "14 Redondo",
    3: "16 Redondo",
    4: "18 Redondo",
    5: "20 Redondo",
    11: "14x25 Retangular",
    6: "16x22 Retangular",
    12: "16x25 Retangular",
    7: "17x25 Retangular",
    8: "18x25 Retangular",
    9: "19x25 Retangular",
    13: "21x25 Retangular",
    14: "16x16 Quadrado",
  };

  const wireDescriptions: WireDescriptions = {
    1: "Niti",
    2: "Aço",
    3: "Niti Curva Reversa",
    4: "Aço Curva Reversa",
    5: "Niti Termoativado",
  };
  const formatDateTime = (dateTimeString: string): string => {
    const dateTime: Date = new Date(dateTimeString);
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    };

    const formattedDateTime: string = dateTime.toLocaleDateString(
      "pt-BR",
      options
    );

    const today: Date = new Date();

    // Verifica se a data é hoje
    if (dateTime.toDateString() === today.toDateString()) {
      return `Hoje, ${formattedDateTime.split(",")[1]}`;
    }
    // Verifica se a data é ontem
    else if (
      dateTime.toDateString() ===
      new Date(today.getTime() - 86400000).toDateString()
    ) {
      return `Ontem, ${formattedDateTime.split(",")[1]}`;
    }
    // Para outras datas
    else {
      return formattedDateTime;
    }
  };

  const getTypeDescription = (typeId: number): string => {
    const description = typeMap[typeId];
    return description ? description : "Descrição não encontrada";
  };

  // Anamnese: prioriza a do cadastro do paciente e usa a da ficha como alternativa
  const anamneseRecord = records.find(hasAnamnese);
  const anamneseItems = patientAnamnese?.items ?? anamneseRecord?.doc?.anamnese;
  const anamneseDate = patientAnamnese
    ? formatAnamneseDate(patientAnamnese.date)
    : anamneseRecord?.appointment;

  return (
    <Container maxWidth="xl">
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <ReturnButton title="Ficha Clínica" onClick={onClick} />

          {hasIncomplete && (
            <Alert
              severity="warning"
              sx={{ marginTop: "12px", marginBottom: "20px" }}
            >
              Você tem assinaturas pendentes.
            </Alert>
          )}

          {/* Anamnese do paciente */}
          {anamneseItems && anamneseItems.length > 0 && (
            <Box sx={{ marginTop: "12px" }}>
              <Button
                variant="contained"
                startIcon={<FaNotesMedical />}
                onClick={() => setOpenAnamnese(true)}
                sx={{ backgroundColor: "#053779" }}
              >
                Visualizar Anamnese
              </Button>
            </Box>
          )}

          {/* Filtro por tipo de consulta */}
          {records.length > 0 && (
            <Styled.FilterContainer>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <FaFilter size={14} color="#666" />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Filtrar por tipo:
                </Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="filter-type-label">Tipo de Consulta</InputLabel>
                <Select
                  labelId="filter-type-label"
                  id="filter-type"
                  value={filterType}
                  label="Tipo de Consulta"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="Todos">
                    <em>Todos os tipos</em>
                  </MenuItem>
                  {availableTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            backgroundColor: getConsultationTypeColor(type),
                          }}
                        />
                        {type}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {filterType !== "Todos" && (
                <Typography variant="caption" color="text.secondary">
                  Exibindo {filteredRecords.length} de {records.length} registros
                </Typography>
              )}
            </Styled.FilterContainer>
          )}

          <Styled.BoxScroll isModal={idPacientParam ? true : false}>
            {filteredRecords.length > 0 ? (
              filteredRecords.map((record, index) => (
                <React.Fragment key={`idx_${index}`}>
                  <Styled.BoxAccordion
                    key={index}
                    sx={{ marginTop: "30px" }}
                    isWarning={record.hasIncompleteSignatures}
                  >
                    <AccordionSummary
                      expandIcon={<IoIosArrowUp />}
                      aria-controls={String(record.idRecord)}
                      id={String(record.idRecord)}
                      sx={{
                        fontWeight: "bold",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          width: "100%",
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography sx={{ fontWeight: "bold" }}>
                          {record.appointment}
                        </Typography>
                        {record.consultationType && (
                          <Chip
                            label={record.consultationType}
                            size="small"
                            sx={{
                              backgroundColor: getConsultationTypeColor(
                                record.consultationType
                              ),
                              color: "white",
                              fontWeight: 500,
                              fontSize: "0.75rem",
                            }}
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Styled.Box>
                        {record.doc &&
                        record.doc.diagnosis &&
                        record.doc.diagnosis.length > 0 ? (
                          <>
                            <Styled.BoxClinical>
                              <Typography variant="h5">Diagnóstico</Typography>
                              {record.doc.diagnosis.map(
                                (item: any, index: number) => (
                                  <React.Fragment key={index}>
                                    <Typography>
                                      {item.question}
                                      {item.description &&
                                        `: ${item.description}`}
                                    </Typography>
                                  </React.Fragment>
                                )
                              )}
                            </Styled.BoxClinical>
                          </>
                        ) : (
                          ""
                        )}

                        {record.doc &&
                        record.doc.anamnese &&
                        record.doc.anamnese.length > 0 ? (
                          <>
                            <Styled.BoxClinical>
                              <Typography variant="h5">Anamnese</Typography>

                              <AnamneseItems items={record.doc.anamnese} />
                            </Styled.BoxClinical>
                          </>
                        ) : (
                          ""
                        )}

                        {record.doc &&
                        record.doc.procedures &&
                        record.doc.procedures.length > 0 ? (
                          <>
                            <Styled.BoxClinical>
                              <Typography variant="h5">
                                Procedimentos Realizados
                              </Typography>

                              {record.doc.procedures.map(
                                (item: any, index: number) =>
                                  item && item.type && item.type !== "NOT" ? (
                                    <Styled.BoxItens key={index}>
                                      {item &&
                                        typeof item.question === "string" && (
                                          <Typography
                                            sx={{
                                              fontWeight: "bold !important",
                                            }}
                                          >
                                            {item.question.replace(/\?/g, "")}
                                          </Typography>
                                        )}

                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                        }}
                                      >
                                        {item &&
                                          item.description &&
                                          (Array.isArray(item.description) ? (
                                            item.description.length > 0 && (
                                              <>
                                                {item.description.map(
                                                  (desc: any, index: any) => (
                                                    <Typography key={index}>
                                                      {desc &&
                                                      typeof desc === "string"
                                                        ? desc
                                                        : desc?.label}
                                                    </Typography>
                                                  )
                                                )}
                                              </>
                                            )
                                          ) : (
                                            <Typography>
                                              {item.description}
                                            </Typography>
                                          ))}
                                      </Box>

                                      {item && item.realize && (
                                        <Typography>{item.realize}</Typography>
                                      )}

                                      {item && item.type && (
                                        <Typography>{item.type}</Typography>
                                      )}

                                      {item.metaData?.wire && (
                                        <Typography>
                                          Material:{" "}
                                          {typeof item.metaData.wire ===
                                          "number"
                                            ? wireDescriptions[
                                                item.metaData.wire
                                              ]
                                            : item.metaData.wire}
                                        </Typography>
                                      )}

                                      {item.metaData?.type && (
                                        <Typography>
                                          Número:{" "}
                                          {typeof item.metaData.type ===
                                          "number"
                                            ? typeDescriptions[
                                                item.metaData.type
                                              ]
                                            : item.metaData.type}
                                        </Typography>
                                      )}

                                      {item?.metaData?.material && (
                                        <Typography>
                                          Material:{" "}
                                          {typeof item.metaData.material ===
                                          "number"
                                            ? wireDescriptions[
                                                item.metaData.material
                                              ]
                                            : item.metaData.material}
                                        </Typography>
                                      )}

                                      {item.metaData?.number && (
                                        <Typography>
                                          Número:{" "}
                                          {typeof item.metaData.number ===
                                          "number"
                                            ? typeDescriptions[
                                                item.metaData.number
                                              ]
                                            : item.metaData.number}
                                        </Typography>
                                      )}

                                      {item.metaData?.observation && (
                                        <Typography>
                                          Observação:{" "}
                                          {item.metaData.observation}
                                        </Typography>
                                      )}

                                      {item?.observation && (
                                        <Typography>
                                          Observação: {item.observation}
                                        </Typography>
                                      )}

                                      {item?.description &&
                                        item?.question === "Manter fio" && (
                                          <Typography>
                                            {item.description}
                                          </Typography>
                                        )}
                                    </Styled.BoxItens>
                                  ) : item ? (
                                    <Styled.BoxItens>
                                      {item &&
                                        typeof item.question === "string" && (
                                          <Typography
                                            sx={{
                                              fontWeight: "bold !important",
                                            }}
                                          >
                                            {item.question.replace(/\?/g, "")}
                                          </Typography>
                                        )}

                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                        }}
                                      >
                                        {item &&
                                          item.description &&
                                          (Array.isArray(item.description) ? (
                                            item.description.length > 0 && (
                                              <>
                                                {item.description.map(
                                                  (desc: any, index: any) => (
                                                    <Typography key={index}>
                                                      {desc &&
                                                      typeof desc === "string"
                                                        ? desc
                                                        : desc?.label}
                                                    </Typography>
                                                  )
                                                )}
                                              </>
                                            )
                                          ) : (
                                            <Typography>
                                              {item.description}
                                            </Typography>
                                          ))}
                                      </Box>

                                      {item && item.realize && (
                                        <Typography>{item.realize}</Typography>
                                      )}

                                      {item.metaData?.wire && (
                                        <Typography>
                                          Material:{" "}
                                          {typeof item.metaData.wire ===
                                          "number"
                                            ? wireDescriptions[
                                                item.metaData.wire
                                              ] ?? item.metaData.wire
                                            : item.metaData.wire}
                                        </Typography>
                                      )}

                                      {item.metaData?.type && (
                                        <Typography>
                                          Número:{" "}
                                          {typeof item.metaData.type ===
                                          "number"
                                            ? typeDescriptions[
                                                item.metaData.type
                                              ] ?? item.metaData.type
                                            : item.metaData.type}
                                        </Typography>
                                      )}

                                      {item?.metaData?.material && (
                                        <Typography>
                                          Material:{" "}
                                          {typeof item.metaData.material ===
                                          "number"
                                            ? wireDescriptions[
                                                item.metaData.material
                                              ] ?? item.metaData.material
                                            : item.metaData.material}
                                        </Typography>
                                      )}

                                      {item.metaData?.number && (
                                        <Typography>
                                          Número:{" "}
                                          {typeof item.metaData.number ===
                                          "number"
                                            ? typeDescriptions[
                                                item.metaData.number
                                              ] ?? item.metaData.number
                                            : item.metaData.number}
                                        </Typography>
                                      )}

                                      {item.metaData?.observation && (
                                        <Typography>
                                          Observação:{" "}
                                          {item.metaData.observation}
                                        </Typography>
                                      )}

                                      {item?.observation && (
                                        <Typography>
                                          Observação: {item.observation}
                                        </Typography>
                                      )}

                                      {item?.description &&
                                        item?.question === "Manter fio" && (
                                          <Typography>
                                            {item.description}
                                          </Typography>
                                        )}
                                    </Styled.BoxItens>
                                  ) : null
                              )}

                              {record.doc &&
                                record.doc.planningPreviousConsultation &&
                                record.doc.planningPreviousConsultation.map(
                                  (item: any, index: any) => {
                                    let descriptionData = [];
                                    if (item.description) {
                                      try {
                                        descriptionData = JSON.parse(
                                          item.description
                                        );
                                      } catch (error) {}
                                    }

                                    return (
                                      <Styled.BoxItens key={index}>
                                        <Typography
                                          sx={{ fontWeight: "bold !important" }}
                                        >
                                          {getDescription(item.id)}
                                        </Typography>

                                        {item.position !== undefined && (
                                          <Typography>
                                            {item.position === 0
                                              ? "Inferior"
                                              : item.position === 1
                                              ? "Superior"
                                              : item.position === 3
                                              ? "Superior e Inferior"
                                              : ""}
                                          </Typography>
                                        )}

                                        {item.metaData?.wire && (
                                          <Typography>
                                            Material:{" "}
                                            {wireDescriptions[
                                              item.metaData.wire
                                            ] || ""}
                                          </Typography>
                                        )}

                                        {item.metaData?.type && (
                                          <Typography>
                                            Número:{" "}
                                            {typeDescriptions[
                                              item.metaData.type
                                            ] || ""}
                                          </Typography>
                                        )}

                                        {descriptionData.length > 0 && (
                                          <>
                                            {descriptionData.map(
                                              (
                                                descItem: any,
                                                descIndex: any
                                              ) => (
                                                <Typography key={descIndex}>
                                                  {descItem.label}
                                                </Typography>
                                              )
                                            )}
                                          </>
                                        )}
                                      </Styled.BoxItens>
                                    );
                                  }
                                )}
                            </Styled.BoxClinical>
                          </>
                        ) : (
                          ""
                        )}

                        {record &&
                          record.nextAppointment &&
                          record.nextAppointment.length > 0 &&
                          record.nextAppointment.map((item, index) => {
                            return (
                              <Styled.BoxClinical key={index}>
                                <Typography variant="h5">
                                  Planejamento da próxima consulta
                                </Typography>
                                <Styled.BoxItens>
                                  <Typography
                                    sx={{ fontWeight: "bold !important" }}
                                  >
                                    {getDescription(item.id)}
                                  </Typography>

                                  {(item.position === 0 ||
                                    item.position === 1 ||
                                    item.position === 3) && (
                                    <Typography key={item.id}>
                                      {positionDescriptions[item.position]}
                                    </Typography>
                                  )}

                                  {typeof item.metaData === "object" &&
                                    !Array.isArray(item.metaData) && (
                                      <>
                                        {item.metaData.wire !== undefined && (
                                          <Typography>
                                            Material:{" "}
                                            {typeof item.metaData.wire ===
                                            "boolean"
                                              ? Object.values(
                                                  wireDescriptions
                                                )[0]
                                              : wireDescriptions[
                                                  item.metaData.wire
                                                ] || ""}
                                          </Typography>
                                        )}

                                        {item.metaData.type !== undefined && (
                                          <Typography>
                                            Número:{" "}
                                            {typeof item.metaData.type ===
                                            "boolean"
                                              ? Object.values(
                                                  typeDescriptions
                                                )[0]
                                              : typeDescriptions[
                                                  item.metaData.type
                                                ] || ""}
                                          </Typography>
                                        )}
                                      </>
                                    )}

                                  {Array.isArray(item.metaData) &&
                                    item.metaData.length > 0 && (
                                      <>
                                        {item.metaData.map(
                                          (descItem, descIndex) => (
                                            <Typography key={descIndex}>
                                              {descItem.label}
                                            </Typography>
                                          )
                                        )}
                                      </>
                                    )}

                                  {item.activation && (
                                    <Typography>
                                      {item.activation === 1
                                        ? "Com ativação"
                                        : "Sem ativação"}
                                    </Typography>
                                  )}

                                  {item.description && (
                                    <Typography>
                                      Observação: {item.description}
                                    </Typography>
                                  )}
                                </Styled.BoxItens>
                              </Styled.BoxClinical>
                            );
                          })}

                        {record.doc &&
                          record.doc.schedule &&
                          record.doc.schedule.length > 0 &&
                          record.doc.schedule.map((item: any, index: any) => (
                            <Styled.BoxClinical key={index}>
                              <Typography variant="h5">
                                Agendamento -{" "}
                                {getTypeDescription(item.procedure)}
                              </Typography>
                              <Typography>
                                {formatDateTime(item.date)}
                              </Typography>
                            </Styled.BoxClinical>
                          ))}

                        {record.fotos && record.fotos.length > 0 && (
                          <Styled.BoxClinical>
                            <Typography variant="h5">Fotos</Typography>
                            <GalleryDocuments
                              images={record.fotos.map((foto, index) => ({
                                src: foto,
                                original: foto,
                                width: 620,
                                height: 580,
                                caption: "Foto " + (index + 1),
                                key: index,
                              }))}
                            />
                          </Styled.BoxClinical>
                        )}

                        {record.dentSignature.length > 0 && (
                          <Styled.BoxClinical>
                            <Typography variant="h5">
                              Assinatura do Dentista
                            </Typography>
                            <Styled.BoxImage src={record.dentSignature} />
                          </Styled.BoxClinical>
                        )}

                        {record.pacSignature.length > 0 && (
                          <Styled.BoxClinical>
                            <Typography variant="h5">
                              Assinatura do Paciente
                            </Typography>
                            <Styled.BoxImage src={record.pacSignature} />
                          </Styled.BoxClinical>
                        )}

                        {record.hasIncompleteSignatures && (
                          <Styled.BoxClinical>
                            <Button onClick={() => setRecordOpen(record)}>
                              Assinar itens pendentes
                            </Button>
                          </Styled.BoxClinical>
                        )}
                      </Styled.Box>
                    </AccordionDetails>
                  </Styled.BoxAccordion>
                </React.Fragment>
              ))
            ) : (
              <Box sx={{ textAlign: "center", marginTop: "20px" }}>
                <Typography sx={{ fontWeight: "bold", fontSize: "20px" }}>
                  {filterType !== "Todos" && records.length > 0
                    ? `Nenhum registro encontrado para o filtro "${filterType}".`
                    : "Não há ficha clínica para este paciente."}
                </Typography>
                {filterType !== "Todos" && records.length > 0 && (
                  <Button
                    variant="text"
                    onClick={() => setFilterType("Todos")}
                    sx={{ marginTop: "10px" }}
                  >
                    Limpar filtro
                  </Button>
                )}
              </Box>
            )}

            <Modal
              open={recordOpen.idRecord > 0}
              onClose={() => setRecordOpen({} as ClinicalRecord)}
            >
              <Box
                sx={{
                  backgroundColor: "white",
                  height: "100%",
                  width: "100%",
                }}
              >
                <ModalSignature
                  onClick={() => setRecordOpen({} as ClinicalRecord)}
                  onLoad={clinicalRecordsAll}
                  typeSignatures={recordOpen.missingSignatures}
                  idRecord={recordOpen.id}
                />
              </Box>
            </Modal>

            <Modal open={openAnamnese} onClose={() => setOpenAnamnese(false)}>
              <Box
                sx={{
                  backgroundColor: "white",
                  height: "100%",
                  width: "100%",
                  overflowY: "auto",
                }}
              >
                <Container maxWidth="xl">
                  <ReturnButton
                    title="Anamnese"
                    onClick={() => setOpenAnamnese(false)}
                  />

                  {anamneseItems && anamneseItems.length > 0 && (
                    <Styled.BoxScroll isModal>
                      {anamneseDate && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ marginTop: "12px" }}
                        >
                          Registrada em: {anamneseDate}
                        </Typography>
                      )}

                      <Styled.BoxClinical>
                        <AnamneseItems items={anamneseItems} />
                      </Styled.BoxClinical>
                    </Styled.BoxScroll>
                  )}
                </Container>
              </Box>
            </Modal>
          </Styled.BoxScroll>
        </>
      )}
    </Container>
  );
};

interface ISimplifiedProps {
  idPacientParam?: number;
}

const SimplifiedClinicalRecord: React.FC<ISimplifiedProps> = ({
  idPacientParam,
}) => {
  const { user } = useAuth();
  const { idPacient } = useParams();
  const [records, setRecords] = React.useState<ClinicalRecord[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);

  const pacId = idPacientParam || Number(idPacient);

  const wireDescriptions: WireDescriptions = {
    1: "Niti",
    2: "Aço",
    3: "Niti Curva Reversa",
    4: "Aço Curva Reversa",
    5: "Niti Termoativado",
  };

  const typeDescriptions: WireDescriptions = {
    1: "12 Redondo",
    2: "14 Redondo",
    3: "16 Redondo",
    4: "18 Redondo",
    5: "20 Redondo",
    6: "16x22 Retangular",
    7: "17x25 Retangular",
    8: "18x25 Retangular",
    9: "19x25 Retangular",
    10: "10 Redondo",
    11: "14x25 Retangular",
    12: "16x25 Retangular",
    13: "21x25 Retangular",
    14: "16x16 Quadrado",
  };

  React.useEffect(() => {
    const fetchRecords = async () => {
      try {
        const { status, data } = await getClinicalRecords({
          userId: user.data.id,
          idPac: pacId,
        });
        if ([200, 201].includes(status) && data.success) {
          setRecords(data.data);
        }
      } catch (error) {
        console.error("Erro ao buscar fichas:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchRecords();
  }, [user.data.id, pacId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: "20px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (records.length === 0) {
    return (
      <Box sx={{ padding: "20px" }}>
        <Typography>Nenhuma ficha clínica encontrada.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: "16px" }}>
      <Typography variant="h5" sx={{ marginBottom: "16px", fontWeight: "bold" }}>
        Ficha Resumida - Procedimentos Realizados
      </Typography>
      {records.map((record, idx) => (
        <Box
          key={idx}
          sx={{
            marginBottom: "12px",
            padding: "12px",
            borderRadius: "8px",
            backgroundColor: "#f7f8fa",
            borderLeft: "4px solid #053779",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
            }}
          >
            <Typography sx={{ fontWeight: "bold", color: "#053779" }}>
              {record.appointment}
            </Typography>
            <Chip
              label={
                (record.procedureId ? typeMap[record.procedureId] : undefined) ||
                normalizeConsultationType(record.procedureName ?? "") ||
                "Procedimento"
              }
              size="small"
              sx={{
                backgroundColor: getConsultationTypeColor(
                  normalizeConsultationType(record.procedureName ?? "")
                ),
                color: "#fff",
                fontSize: "0.7rem",
              }}
            />
          </Box>
          {record.doc?.procedures
            ?.filter(
              (item: any) =>
                item &&
                item.question &&
                item.question !== "Observações" &&
                item.question !== "Campo aberto (Observações gerais)"
            )
            .map((item: any, i: number) => (
              <Box key={i} sx={{ marginLeft: "8px", marginBottom: "4px" }}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {item.question}
                </Typography>
                {item.description && (
                  <Typography variant="body2" sx={{ color: "#555" }}>
                    {Array.isArray(item.description)
                      ? item.description
                          .map((d: any) =>
                            typeof d === "string" ? d : d?.label
                          )
                          .join(", ")
                      : item.description}
                  </Typography>
                )}
                {item.metaData?.wire && (
                  <Typography variant="body2" sx={{ color: "#555" }}>
                    Material:{" "}
                    {typeof item.metaData.wire === "number"
                      ? wireDescriptions[item.metaData.wire] ?? item.metaData.wire
                      : item.metaData.wire}
                    {item.metaData?.type &&
                      ` | Número: ${
                        typeof item.metaData.type === "number"
                          ? typeDescriptions[item.metaData.type] ?? item.metaData.type
                          : item.metaData.type
                      }`}
                  </Typography>
                )}
                {item.metaData?.material && (
                  <Typography variant="body2" sx={{ color: "#555" }}>
                    Material:{" "}
                    {typeof item.metaData.material === "number"
                      ? wireDescriptions[item.metaData.material] ??
                        item.metaData.material
                      : item.metaData.material}
                    {item.metaData?.number &&
                      ` | Número: ${
                        typeof item.metaData.number === "number"
                          ? typeDescriptions[item.metaData.number] ??
                            item.metaData.number
                          : item.metaData.number
                      }`}
                  </Typography>
                )}
                {item.metaData?.observation && (
                  <Typography variant="body2" sx={{ color: "#555" }}>
                    Observação: {item.metaData.observation}
                  </Typography>
                )}
                {item.observation && (
                  <Typography variant="body2" sx={{ color: "#555" }}>
                    Observação: {item.observation}
                  </Typography>
                )}
              </Box>
            ))}
        </Box>
      ))}
    </Box>
  );
};

export { ClinicalRecordUser, SimplifiedClinicalRecord };
